import { useNavigate } from '@tanstack/react-router'
import { CantileverButton } from 'components/CantileverButton'
import { getSearchResultUrl } from 'features/search'
import { X } from 'lucide-react'
import React from 'react'
import styled from 'styled-components'
import { GeckoEntry, RolodexEntryType } from 'types'

type ObjectChipPropsWithEntry = {
  entry: GeckoEntry
  siteID?: string
  entryID?: never
  orgID?: never
  isEditMode: boolean
  onClick?: () => void
  onDelete?: () => void
}

type ObjectChipPropsWithIDs = {
  entry?: never
  siteID?: never
  entryID: string
  orgID: string
  displayName: string
  displayType: RolodexEntryType
  isEditMode: boolean
  onClick?: () => void
  onDelete?: () => void
}

export type ObjectChipProps = ObjectChipPropsWithEntry | ObjectChipPropsWithIDs

const ObjectChipContainer = styled.span<{ $hasDelete: boolean }>`
  background: ${({ theme }) => theme.forecast.cardGroupBackground};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  color: ${({ theme }) => theme.forecast.defaultText};
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  font-weight: 500;
  font-size: 0.875rem;
  text-decoration: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  margin: 0 0.125rem;
  white-space: nowrap;
  max-width: 12.5rem;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    border-color: ${({ theme }) => theme.forecast.primary};
    box-shadow: 0 0 0 1px ${({ theme }) => theme.forecast.primary}20;
  }
`

const ObjectChipIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  background: ${({ theme }) => theme.forecast.primary}20;
  color: ${({ theme }) => theme.forecast.primary};
  border-radius: 50%;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
`

const ChipText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const StyledDeleteButton = styled(CantileverButton)`
  min-height: 1rem;
  width: 1rem;
  opacity: 0;
  transition: opacity 0.15s ease;
  flex-shrink: 0;
  margin-left: 0.25rem;

  ${ObjectChipContainer}:hover & {
    opacity: 1;
  }
`

export const ObjectChip: React.FC<ObjectChipProps> = (props) => {
  const { isEditMode, onClick, onDelete } = props
  const navigate = useNavigate()

  const handleClick = () => {
    if (isEditMode && onClick) {
      onClick()
    } else if (!isEditMode) {
      if (props.entry && props.siteID) {
        const navigationInfo = getSearchResultUrl(props.entry, props.siteID)
        if (navigationInfo) {
          navigate({
            to: navigationInfo.to,
            params: navigationInfo.params,
            search: navigationInfo.search,
          })
        }
      } else if (props.entryID && props.orgID) {
        navigate({
          to: '/prism/$orgID/$entryId',
          params: { orgID: props.orgID, entryId: props.entryID },
        })
      }
    }
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDelete?.()
  }

  const getIcon = (type: RolodexEntryType): string => {
    return type.charAt(0).toUpperCase()
  }

  const entryName = props.entry
    ? ((props.entry.data.name as string | undefined) ??
      `${props.entry.type} ${props.entry.id}`)
    : props.displayName

  const entryType = props.entry ? props.entry.type : props.displayType

  return (
    <ObjectChipContainer
      $hasDelete={!!onDelete}
      onClick={handleClick}
      title={`${entryType}: ${entryName}`}>
      <ObjectChipIcon>{getIcon(entryType)}</ObjectChipIcon>
      <ChipText>{entryName}</ChipText>
      {onDelete && (
        <StyledDeleteButton
          iconOnly
          ghost
          danger
          onClick={handleDelete}
          title='Remove'>
          <X size={10} />
        </StyledDeleteButton>
      )}
    </ObjectChipContainer>
  )
}
